import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  NodeProps,
  Handle,
  Position,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Drawer,
  Divider,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  FitScreen as FitScreenIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { paaTreeApiService, PAATreeNode, PAATree } from '../../services/paaTreeApiService';

interface PAANodeData {
  label: string;
  isRoot?: boolean;
  depth: number;
  hasAnswer: boolean;
  questionId?: number;
  answerText?: string;
  sourceUrl?: string;
  sourceTitle?: string;
  onNodeClick?: (data: any) => void;
}

// Custom node component for PAA questions
const PAAQuestionNode: React.FC<NodeProps> = ({ data }) => {
  const [copied, setCopied] = useState(false);
  const nodeData = data as unknown as PAANodeData;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(nodeData.label);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const nodeStyle = {
    root: {
      bgcolor: '#7b61ff',
      color: 'white',
      fontWeight: 600,
      fontSize: '16px',
      borderColor: 'transparent',
    },
    level0: {
      bgcolor: '#f3f0ff',
      borderColor: '#7b61ff',
      color: '#333',
      fontSize: '14px',
    },
    level1: {
      bgcolor: '#fff7ed',
      borderColor: '#ff9500',
      color: '#333',
      fontSize: '14px',
    },
    level2: {
      bgcolor: '#f0fdf4',
      borderColor: '#22c55e',
      color: '#333',
      fontSize: '14px',
    },
    level3: {
      bgcolor: '#fef2f2',
      borderColor: '#ef4444',
      color: '#333',
      fontSize: '14px',
    },
  };

  const style = nodeData.isRoot ? nodeStyle.root : nodeStyle[`level${nodeData.depth}` as keyof typeof nodeStyle] || nodeStyle.level0;

  return (
    <Box
      sx={{
        p: 2,
        minWidth: 250,
        maxWidth: 350,
        borderRadius: nodeData.isRoot ? 3 : 2,
        border: nodeData.isRoot ? 'none' : `2px solid ${style.borderColor}`,
        bgcolor: style.bgcolor,
        color: style.color,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      }}
      onClick={() => nodeData.onNodeClick?.(nodeData)}
    >
      {!nodeData.isRoot && (
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: '#555' }}
        />
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: nodeData.isRoot ? 600 : 500,
            fontSize: style.fontSize,
            lineHeight: 1.5,
          }}
        >
          {nodeData.label}
        </Typography>
        {!nodeData.isRoot && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            sx={{ 
              p: 0.5,
              color: copied ? 'success.main' : 'text.secondary',
            }}
          >
            <CopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {nodeData.hasAnswer && (
        <Chip
          label="Has Answer"
          size="small"
          sx={{ mt: 1, height: 20, fontSize: '11px' }}
        />
      )}

      {nodeData.isRoot ? (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: '#555' }}
        />
      ) : (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            style={{ background: '#555' }}
          />
        </>
      )}
    </Box>
  );
};

const nodeTypes = {
  paaQuestion: PAAQuestionNode,
};

export const PAATreeViewer: React.FC = () => {
  const { treeId } = useParams<{ treeId: string }>();
  const navigate = useNavigate();
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [treeInfo, setTreeInfo] = useState<PAATree | null>(null);
  const [selectedNode, setSelectedNode] = useState<PAANodeData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (treeId) {
      loadTree(parseInt(treeId));
    }
  }, [treeId]);

  const loadTree = async (id: number) => {
    setLoading(true);
    setError(null);
    
    try {
      // Load tree info and structure
      const [treeInfoResult, treeStructureResult] = await Promise.all([
        paaTreeApiService.getTreeById(id),
        paaTreeApiService.buildTreeStructure(id)
      ]);

      if (treeInfoResult.error || treeStructureResult.error) {
        throw new Error(treeInfoResult.error || treeStructureResult.error || 'Unknown error');
      }

      setTreeInfo(treeInfoResult.tree);
      
      if (treeStructureResult.tree) {
        const { nodes: flowNodes, edges: flowEdges } = convertToFlowElements(
          treeStructureResult.tree
        );
        setNodes(flowNodes as any);
        setEdges(flowEdges as any);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load tree');
    } finally {
      setLoading(false);
    }
  };

  const convertToFlowElements = (tree: PAATreeNode) => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const levelWidth = 400;
    const levelHeight = 150;
    
    // Calculate positions for each level
    const nodesPerLevel = new Map<number, number>();
    const currentPositionInLevel = new Map<number, number>();
    
    // First pass: count nodes per level
    const countNodes = (node: PAATreeNode) => {
      const level = node.depth_level + 1; // +1 because root is -1
      nodesPerLevel.set(level, (nodesPerLevel.get(level) || 0) + 1);
      node.children.forEach(countNodes);
    };
    countNodes(tree);
    
    // Second pass: create nodes and edges
    const processNode = (node: PAATreeNode, parentId?: string) => {
      const level = node.depth_level + 1;
      const totalAtLevel = nodesPerLevel.get(level) || 1;
      const positionIndex = currentPositionInLevel.get(level) || 0;
      currentPositionInLevel.set(level, positionIndex + 1);
      
      // Calculate x position to center nodes at each level
      const levelStartX = -(totalAtLevel - 1) * levelWidth / 2;
      const x = levelStartX + positionIndex * levelWidth;
      const y = level * levelHeight;
      
      const flowNode: Node = {
        id: node.id,
        type: 'paaQuestion',
        position: { x, y },
        data: {
          label: node.text,
          isRoot: node.isRoot,
          depth: node.depth_level,
          hasAnswer: !!node.answer_text,
          questionId: node.question_id,
          answerText: node.answer_text,
          sourceUrl: node.source_url,
          sourceTitle: node.source_title,
          onNodeClick: (data: PAANodeData) => {
            setSelectedNode(data);
            setDrawerOpen(true);
          },
        },
      };
      
      nodes.push(flowNode);
      
      if (parentId) {
        edges.push({
          id: `${parentId}-${node.id}`,
          source: parentId,
          target: node.id,
          type: 'smoothstep',
          animated: false,
          style: {
            strokeWidth: 2,
            stroke: '#999',
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: '#999',
          },
        });
      }
      
      // Process children
      node.children.forEach(child => processNode(child, node.id));
    };
    
    processNode(tree);
    
    return { nodes, edges };
  };

  const onFitView = useCallback(() => {
    const flow = document.querySelector('.react-flow__viewport');
    if (flow) {
      // Trigger fit view
      const fitViewButton = document.querySelector('.react-flow__controls-fitview');
      if (fitViewButton) {
        (fitViewButton as HTMLButtonElement).click();
      }
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh' 
      }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/paa-trees')}
        >
          Back to Trees
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 2, borderRadius: 0, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/paa-trees')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {treeInfo?.root_keyword}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {nodes.length} questions • {treeInfo?.country}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FitScreenIcon />}
            onClick={onFitView}
          >
            Fit View
          </Button>
        </Box>
      </Paper>

      {/* Tree Visualization */}
      <Box sx={{ flex: 1 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-left"
        >
          <Background />
          <Controls />
          <Panel position="top-right">
            <Card sx={{ p: 1.5, minWidth: 200 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Tree Legend
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#7b61ff', borderRadius: 1 }} />
                  <Typography variant="caption">Root Keyword</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#f3f0ff', border: '2px solid #7b61ff', borderRadius: 1 }} />
                  <Typography variant="caption">Level 1 Questions</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#fff7ed', border: '2px solid #ff9500', borderRadius: 1 }} />
                  <Typography variant="caption">Level 2 Questions</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#f0fdf4', border: '2px solid #22c55e', borderRadius: 1 }} />
                  <Typography variant="caption">Level 3 Questions</Typography>
                </Box>
              </Box>
            </Card>
          </Panel>
        </ReactFlow>
      </Box>

      {/* Question Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            maxWidth: '100%',
          },
        }}
      >
        {selectedNode && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                Question Details
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Question
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {selectedNode.label}
            </Typography>
            
            {selectedNode.answerText && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Answer Preview
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 3 }}>
                  <Typography variant="body2">
                    {selectedNode.answerText}
                  </Typography>
                </Paper>
              </>
            )}
            
            {selectedNode.sourceUrl && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Source
                </Typography>
                <Link
                  href={selectedNode.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {selectedNode.sourceTitle || selectedNode.sourceUrl}
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </Link>
              </>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};